import React from "react";
import { Plan } from "services/quotes/types";
import { moneyWithComma } from "utils/form/helpers";
import { getGreenEnergyImage, getLogoBySupplier } from "config/suppliers";
import { humanFormatEffectiveDate } from "utils/retail/plans";
import { unitByAnnualUsage } from "utils/data";
import useTheme from "evcharging/hooks/useTheme";
import { css } from "@emotion/css";
import { Theme } from "evcharging/app/themes";
import RatesCardLine from "containers/retail/sections/energy-rates-3/RatesCardLine";
import RatesMoreDetails from "containers/retail/sections/energy-rates-3/RatesMoreDetails";
import RatesSelectPlanButton from "containers/retail/sections/energy-rates-3/RatesSelectPlanButton";
import useI18n from "evcharging/hooks/useI18n";

interface RatesPlanCardProps {
  plan: Plan;
  onSelect: (plan: Plan) => void;
  buttonText?: string;
}

const RatesBestPlanCard: React.FC<RatesPlanCardProps> = (props) => {
  const { t } = useI18n();

  const { plan, onSelect, buttonText = t('rates.select-plan') } = props;
  const theme = useTheme();
  const classes = createStyles(theme);
  const greenEnergyImage = getGreenEnergyImage(plan);

  return (
    <div className={classes.root}>
      <div className={classes.box}>
        {t('rates.n-months', { n: plan.term.value })}
      </div>
      {
        greenEnergyImage && (
          <img
            className={classes.greenEnergy}
            alt={'Green energy'}
            title={'Green energy'}
            src={greenEnergyImage}
          />
        )
      }
      <div className={classes.card}>
        <div className={classes.cardContent}>
          <div className={classes.supplier}>
            <div className={classes.logoWrapper}>
              <img
                className={classes.logo}
                alt={plan.supplier.name}
                title={plan.supplier.name}
                src={getLogoBySupplier(plan.supplier)}
              />
            </div>
            <div className={classes.supplierName}>{plan.supplier.name}</div>
          </div>

          <div className={classes.info}>{t('rates.n-month-price', { value: plan.term.value })}</div>

          <div className={classes.price}>
            {`¢${moneyWithComma(plan.price.amount * 100, 3)}`}
            <span>{t('rates.per-usage', { usage: unitByAnnualUsage(plan.annualUsage)})}</span>
          </div>

          <RatesCardLine
            label={t('rates.contract-start-date')}
            value={humanFormatEffectiveDate(new Date(plan.effectiveDate))}
          />

          <div className={classes.actions}>
            <RatesSelectPlanButton plan={plan} onSelect={onSelect} buttonText={buttonText} />
            <RatesMoreDetails plan={plan} />
          </div>
        </div>
      </div>
    </div>
  );
};

const createStyles = (theme: Theme) => (
  {
    root: css({
      flexGrow: 1,
      position: 'relative',
      'button': {
        width: '100%',
        border: 'none',
        background: 'none',
        padding: 10,
        fontSize: 16,
        fontFamily: "Mulish, sans-serif !important",
        fontWeight: 700,
        lineHeight: 1.75,
        borderRadius: "6px",
        textTransform: "inherit",
        boxShadow: 'none',
        transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        cursor: 'pointer',
      },
    }),
    greenEnergy: css({
      position: 'absolute',
      right: 14,
      top: 8,
    }),
    card: css({
      paddingTop: theme.spacing(5),
      color: "#000000",
      background: "#ffffff",
      boxShadow: "0 11px 23px rgba(0, 0, 0, 0.12)",
      borderRadius: "6px"
    }),
    cardContent: css({
      padding: theme.spacing(2.5),
    }),
    buttonGray: css({
      color: '#93979B',
      '&:hover': {
        backgroundColor: '#f7f8fa',
      },
    }),
    box: css({
      textAlign: 'center',
      width: theme.spacing(20),
      height: theme.spacing(6),
      textTransform: 'capitalize',
      lineHeight: 2.7,
      borderRadius: theme.spacing(1, 1, 1, 0),
      color: '#fff',
      background: '#F0C442',
      fontSize: theme.spacing(2.25),
      fontWeight: 'bold',
      position: 'absolute',
      zIndex: 2,
      left: -10,
      top: -6,
      '&:after': {
        position: 'absolute',
        content: '""',
        display: 'block',
        width: 1,
        height: 1,
        borderWidth: 5,
        bottom: -10,
        left: 0,
        borderStyle: 'solid',
        borderColor: '#D9B43F',
        borderLeftColor: 'transparent',
        borderBottomColor: 'transparent',
      },
    }),
    info: css({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#000',
      fontSize: 12,
      fontWeight: 600,
      border: '1px solid #000',
      borderRadius: 3,
      width: 143,
      height: 27,
    }),
    price: css({
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'center',
      color: '#000',
      fontSize: 32,
      fontWeight: 600,
      borderBottom: '1px solid #E8E8E8',
      paddingTop: 10,
      paddingBottom: 6,
      '& > span': {
        paddingLeft: 8,
        fontSize: 16,
      },
    }),
    actions: css({
      marginTop: 20,
    }),
    logo: css({
      maxHeight: '100%',
      maxWidth: '100%',
    }),
    logoWrapper: css({
      maxWidth: 170,
      margin: '0 auto',
      height: 41,
      marginBottom: theme.spacing(3),
    }),
    supplier: css({
      textAlign: 'center',
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(0.75),
      borderColor: '#ccc',
    }),
    supplierName: css({
      margin: theme.spacing(2, 0, 2.5),
      fontSize: theme.spacing(2.25),
      fontWeight: 'bold',
      lineHeight: 1.24,
      color: "#000000",
    }),
  }
);

export default RatesBestPlanCard;
